import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { Listing } from '@/lib/types';
import { Card, SectionHeader } from '@/components/ui';
import { Trash2, Tag, Calendar, ExternalLink, ShieldCheck, MapPin } from 'lucide-react';

interface RowItem {
  id: string;
  type: 'Offer' | 'Event';
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  promoCode?: string;
  location?: string;
  mapLink?: string;
  image?: string | null;
  bizId: string;
  bizName: string;
  bizCity: string;
}

export default function OffersEventsTracker() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'Offer' | 'Event'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchListings = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.from('listings').select('*');
      setListings((data as Listing[]) ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  // Aggregate all offers & events into a flat list
  const items = useMemo(() => {
    const list: RowItem[] = [];
    listings.forEach(l => {
      const s = l.schema_json as any;
      if (s) {
        const offers = s.offers || [];
        const events = s.events || [];
        
        offers.forEach((item: any) => {
          list.push({
            id: item.id,
            type: 'Offer',
            title: item.title,
            description: item.description,
            startDate: item.startDate,
            endDate: item.endDate,
            promoCode: item.promo_code,
            image: item.image || null,
            bizId: l.id,
            bizName: l.name,
            bizCity: l.city || ''
          });
        });

        events.forEach((item: any) => {
          list.push({
            id: item.id,
            type: 'Event',
            title: item.title,
            description: item.description,
            startDate: item.startDate,
            endDate: item.endDate,
            location: item.event_location,
            mapLink: item.event_map_link,
            image: item.image || null,
            bizId: l.id,
            bizName: l.name,
            bizCity: l.city || ''
          });
        });
      }
    });
    return list;
  }, [listings]);

  // Filter items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (filterType !== 'all' && item.type !== filterType) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.title.toLowerCase().includes(query) ||
          item.bizName.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [items, filterType, searchQuery]);

  // Delete Offer or Event
  const handleDelete = async (row: RowItem) => {
    if (!window.confirm(`Are you sure you want to delete this ${row.type.toLowerCase()}?`)) return;
    
    const targetListing = listings.find(l => l.id === row.bizId);
    if (!targetListing) return;

    try {
      const schema = { ...(targetListing.schema_json as any) };
      if (row.type === 'Offer') {
        schema.offers = (schema.offers || []).filter((o: any) => o.id !== row.id);
      } else {
        schema.events = (schema.events || []).filter((e: any) => e.id !== row.id);
      }

      await supabase.from('listings').update({ schema_json: schema }).eq('id', targetListing.id);
      
      // Update local state in place
      setListings(prev =>
        prev.map(l => l.id === targetListing.id ? { ...l, schema_json: schema } : l)
      );

    } catch (e) {
      console.error(e);
      alert('Error deleting item. Please try again.');
    }
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-3xl min-h-screen text-slate-800">
      <SectionHeader
        title="Offers & Events Manager"
        subtitle={`Track all active promotional offers and special events submitted by business owners.`}
      />

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-slate-50 border border-slate-200 p-4 rounded-2xl">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
              filterType === 'all'
                ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            All Items ({items.length})
          </button>
          <button
            onClick={() => setFilterType('Offer')}
            className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
              filterType === 'Offer'
                ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            Offers Only
          </button>
          <button
            onClick={() => setFilterType('Event')}
            className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
              filterType === 'Event'
                ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            Events Only
          </button>
        </div>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, details or business name..."
          className="w-full sm:w-72 px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:border-emerald-600 outline-none"
        />
      </div>

      {/* Table grid */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">Tagged Business</th>
                <th className="px-5 py-3.5">Offer/Event Title</th>
                <th className="px-5 py-3.5">Promo Details / Location</th>
                <th className="px-5 py-3.5">Dates Duration</th>
                <th className="px-5 py-3.5 text-center">Status</th>
                <th className="px-5 py-3.5 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">Loading tracking lists...</td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">No active offers or events found.</td>
                </tr>
              ) : (
                filteredItems.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50/55 transition">
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        row.type === 'Offer'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {row.type === 'Offer' ? <Tag className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
                        {row.type}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-bold text-slate-900">{row.bizName}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{row.bizCity}</div>
                    </td>
                     <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {row.image && (
                          <img
                            src={row.image}
                            alt=""
                            className="h-10 w-10 rounded-lg object-cover border border-slate-200 bg-slate-50 shrink-0 cursor-zoom-in"
                            onClick={() => window.open(row.image!, '_blank')}
                          />
                        )}
                        <div className="min-w-0 text-left">
                          <div className="text-slate-800 font-bold max-w-xs truncate" title={row.title}>{row.title}</div>
                          <div className="text-[10px] text-slate-400 font-medium max-w-xs line-clamp-1 mt-0.5" title={row.description}>
                            {row.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600">
                      {row.type === 'Offer' ? (
                        row.promoCode ? (
                          <span className="bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded font-mono">
                            CODE: {row.promoCode}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">None</span>
                        )
                      ) : (
                        <div className="max-w-xs space-y-1">
                          <div className="flex items-center gap-0.5 text-[10px] text-slate-800">
                            <MapPin className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            <span className="truncate">{row.location || 'Business Location'}</span>
                          </div>
                          {row.mapLink && (
                            <a
                              href={row.mapLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[9px] text-emerald-600 hover:underline flex items-center gap-0.5 font-bold"
                            >
                              Google Maps Link <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-500 font-mono text-[10px]">
                      {row.startDate} to {row.endDate}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                        <ShieldCheck className="h-3 w-3 text-emerald-700" /> Active
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        onClick={() => handleDelete(row)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition cursor-pointer"
                        title="Delete Deal"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
